import type { PermissionKey } from "@nmt/shared";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { get } from "../api";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { useSession } from "../session";
import { applyTheme } from "../theme";
import { SearchBox } from "./SearchBox";
import { FredButton, FredPanel } from "./fred/FredPanel";
import { useCommStatus } from "../pages/Comms";
import { CallPopups } from "./CallPopups";
import { cx } from "./ui";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  needs?: PermissionKey[];
  /** Also active on these path prefixes. */
  also?: string[];
}

const icon = (d: string) => (
  <svg
    viewBox="0 0 24 24"
    className="size-5 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d={d} />
  </svg>
);

const cameraIcon = (
  <svg
    viewBox="0 0 24 24"
    className="size-6"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13.5" r="3.5" />
  </svg>
);

const NAV: NavItem[] = [
  {
    to: "/",
    label: "Dashboard",
    icon: icon("M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"),
  },
  {
    to: "/jobs",
    label: "Jobs",
    icon: icon("M3 7h18v13H3zM8 7V4h8v3M3 12h18"),
    needs: ["customers.view"],
  },
  {
    to: "/calendar",
    label: "Calendar",
    icon: icon("M4 5h16v15H4zM4 10h16M8 3v4M16 3v4"),
    needs: ["customers.view"],
  },
  {
    to: "/map",
    label: "Map",
    icon: icon("M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14"),
    needs: ["customers.view"],
  },
  {
    to: "/tasks",
    label: "Tasks",
    icon: icon(
      "M9 11l3 3 8-8M20 12v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h11",
    ),
  },
  {
    to: "/inbox",
    label: "Texts and calls",
    icon: icon("M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"),
    needs: ["comms.view"],
  },
  {
    to: "/fred-inbox",
    label: "Fred's inbox",
    icon: icon("M4 6h16v12H4zM4 7l8 6 8-6"),
    needs: ["fred.use"],
  },
  {
    to: "/assistant",
    label: "Assistant",
    icon: icon("M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8zM19 15l.9 2.1L22 18l-2.1.9L19 21l-.9-2.1L16 18l2.1-.9z"),
    needs: ["fred.use"],
  },
  {
    to: "/work-orders",
    label: "Work orders",
    icon: icon(
      "M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4z",
    ),
    needs: ["customers.view"],
  },
  {
    to: "/material-orders",
    label: "Materials",
    icon: icon("M3 7l9-4 9 4-9 4zM3 7v10l9 4 9-4V7M12 11v10"),
    needs: ["customers.view"],
  },
  {
    to: "/receipts",
    label: "Receipts",
    icon: icon("M6 2h12v20l-3-2-3 2-3-2-3 2zM9 7h6M9 11h6M9 15h4"),
    needs: ["receipts.submit", "receipts.approve"],
  },
  {
    to: "/contacts",
    label: "Contacts",
    icon: icon(
      "M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M9 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6M22 19v-1a4 4 0 0 0-3-3.9M16 4.1a3 3 0 0 1 0 5.8",
    ),
    needs: ["customers.view"],
  },
  {
    to: "/documents",
    label: "Documents",
    icon: icon("M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6"),
    needs: ["customers.view"],
  },
  {
    to: "/reports",
    label: "Reports",
    icon: icon("M4 20V10M10 20V4M16 20v-7M22 20H2"),
    // The Reports page's reports (the QuickBooks P&L is on the job).
    needs: ["reports.overview", "reports.leaderboard", "reports.satisfaction", "reports.closeRate", "reports.aging", "reports.pipeline", "reports.leadSources", "reports.jobProfit"],
  },
  {
    to: "/settings",
    label: "Settings",
    icon: icon(
      "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z",
    ),
    // Everyone: their own notifications are under Settings.
  },
];

export function Shell() {
  // On a job, the camera saves to that job; anywhere else it asks which job.
  const where = useLocation().pathname;
  const cameraHref = /^\/jobs\/[0-9a-f-]{36}/.test(where) ? `/camera?job=${where.split("/")[2]}` : "/camera";
  const { me, can, signOut } = useSession();
  const commStatus = useCommStatus(!!me && can("comms.view"));
  const fredInbox = useQuery({ queryKey: ["fred-inbox-count"], queryFn: () => get<{ needsAnswer: number; held: number }>("/api/fred/inbox/count"), enabled: !!me && can("fred.use"), refetchInterval: 60_000 });
  const [open, setOpen] = useState(false);
  const [fred, setFred] = useState(false);
  // A conversation to open in Fred (from Fred's inbox).
  const [fredOpenId, setFredOpenId] = useState<string | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      setFredOpenId((e as CustomEvent<{ conversationId: string }>).detail.conversationId);
      setFred(true);
    };
    window.addEventListener("nmt:open-fred", onOpen);
    return () => window.removeEventListener("nmt:open-fred", onOpen);
  }, []);
  // On a phone the page is hidden while Fred is open: back where you were when he closes.
  const pageY = useRef(0);
  useEffect(() => {
    if (fred) return;
    requestAnimationFrame(() => window.scrollTo(0, pageY.current));
    const onScroll = () => (pageY.current = window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [fred]);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  const fredOk = !!me && can("fred.use");
  // Ctrl+K (⌘K on a Mac) opens and closes Fred from anywhere.
  useEffect(() => {
    if (!fredOk) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setFred((f) => !f);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fredOk]);
  useEffect(() => {
    applyTheme(me?.theme ?? null);
  }, [me?.theme]);
  if (!me) return null;
  const items = NAV.filter((n) => !n.needs || can(...n.needs));
  const unread = commStatus.data?.unread ?? 0;
  const fredWaiting = (fredInbox.data?.needsAnswer ?? 0) + (fredInbox.data?.held ?? 0);

  const nav = (
    <nav className="flex flex-col gap-0.5" aria-label="Main">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          className={({ isActive }) =>
            cx(
              "flex items-center gap-3 rounded-[9px] px-3 py-2.5 text-[14px] font-medium transition-colors",
              isActive
                ? "brand-gradient text-white"
                : "text-white/70 hover:bg-white/10 hover:text-white",
            )
          }
        >
          {item.icon}
          {item.label}
          {item.to === "/fred-inbox" && !!fredWaiting && (
            <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs font-bold text-brand">
              {fredWaiting}
            </span>
          )}
          {item.to === "/inbox" && !!unread && (
            <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-xs font-bold text-brand">
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  // The uploaded logo takes the whole spot, on a white card so any logo reads on the dark sidebar
  // (the same logo goes on white documents). Without one: the icon and name.
  const brand = me.theme.logoUrl ? (
    <div className="px-1">
      <div className="flex h-14 items-center justify-center rounded-[var(--radius-control)] bg-white px-3 py-2">
        <img src={me.theme.logoUrl} alt={me.company.name} className="max-h-full max-w-full object-contain" />
      </div>
    </div>
  ) : (
    <div className="flex items-center gap-2.5 px-2">
      <img src="/icon-192.png" alt="" className="size-8 object-contain" />
      <div className="truncate text-lg font-bold text-white">
        {me.company.name}
      </div>
    </div>
  );

  const account = (
    <div className="border-t border-white/10 pt-4">
      <Link to="/profile" title="My profile" className="flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-1.5 hover:bg-white/10">
        <span
          className="grid size-8 shrink-0 place-items-center rounded-full text-sm font-semibold text-white"
          style={{ background: me.user.color }}
          aria-hidden
        >
          {me.user.firstName.charAt(0)}
          {me.user.lastName.charAt(0)}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">
            {me.user.firstName} {me.user.lastName}
          </div>
          <div className="truncate text-xs text-white/60">My profile</div>
        </div>
      </Link>
      <button
        onClick={signOut}
        className="mt-2 w-full rounded-[var(--radius-control)] px-3 py-2 text-left text-sm text-white/70 hover:bg-white/10 hover:text-white"
      >
        Sign out
      </button>
    </div>
  );

  return (
    <>
      {/* On a phone Fred is full screen: the page is hidden (not unmounted) while he's open, so nothing scrolls behind him. */}
      <div
        className={cx(
          "min-h-dvh lg:grid lg:grid-cols-[232px_1fr]",
          fred && "max-lg:hidden",
        )}
      >
        {/* Desktop sidebar */}
        <aside className="sidebar sticky top-0 hidden h-dvh flex-col justify-between px-3.5 py-5 lg:flex">
          <div className="flex flex-col gap-7">
            {brand}
            {nav}
          </div>
          {account}
        </aside>

        {/* Mobile top bar */}
        <div className="sidebar sticky top-0 z-30 flex items-center justify-between gap-3 px-3 py-2.5 lg:hidden">
          {brand}
          <span className="flex-1" />
          {fredOk && (
            <FredButton dark onClick={() => (setOpen(false), setFred(true))} />
          )}
          {can("files.upload") && (
            <Link
              to={cameraHref}
              aria-label="Take photos"
              className="rounded-md p-2 text-white hover:bg-white/10"
            >
              {cameraIcon}
            </Link>
          )}
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-md p-2 text-white hover:bg-white/10"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
          >
            <svg
              viewBox="0 0 24 24"
              className="size-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              {open ? (
                <path d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path d="M4 7h16M4 12h16M4 17h16" />
              )}
            </svg>
          </button>
        </div>
        {open && (
          <div className="sidebar fixed inset-x-0 top-[56px] bottom-0 z-20 flex flex-col justify-between px-3.5 py-4 lg:hidden">
            {nav}
            {account}
          </div>
        )}

        <div className="min-w-0">
          {me.stage === "staging" && (
            <div className="bg-wait-wash px-4 py-1.5 text-center text-sm font-medium text-wait">
              Staging: test data only. Nothing here is sent to customers or
              QuickBooks.
            </div>
          )}
          {can("customers.view") && (
            <div className="sticky top-[56px] z-10 border-b border-line bg-paper/95 px-4 py-2.5 backdrop-blur sm:px-8 lg:top-0">
              <div className="mx-auto flex max-w-6xl items-center gap-2">
                <div className="min-w-0 flex-1">
                  <SearchBox />
                </div>
                {fredOk && (
                  <FredButton
                    className="hidden lg:flex"
                    onClick={() => setFred((f) => !f)}
                  />
                )}
                {can("files.upload") && (
                  <Link
                    to={cameraHref}
                    aria-label="Take photos"
                    title="Take photos"
                    className="hidden rounded-[var(--radius-control)] border border-line bg-white p-2 text-ink-soft hover:border-brand hover:text-brand lg:block"
                  >
                    {cameraIcon}
                  </Link>
                )}
              </div>
            </div>
          )}
          <main className={cx("mx-auto w-full px-4 py-6 sm:px-8 lg:py-8", location.pathname === "/map" ? "max-w-none" : "max-w-6xl")}>
            <Outlet />
          </main>
        </div>
      </div>
      {fredOk && <FredPanel open={fred} openId={fredOpenId} onClose={() => (setFred(false), setFredOpenId(null))} />}
      <CallPopups />
    </>
  );
}
